import type { NegativeMatchType } from "@adpilot/shared";
import { IconSearch, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditorEmptyState } from "@/components/campaign-creation/wizard/EditorEmptyState";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldGroup, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type NegativeKeywordTarget,
  parseNegativeKeywords,
  parseNegativeProducts,
} from "@/lib/campaign-creation-state";
import { cn } from "@/lib/utils";

export function NegativeKeywordEditor({
  value,
  input,
  onInputChange,
  max,
  onChange,
}: {
  value: NegativeKeywordTarget[];
  input: string;
  onInputChange: (input: string) => void;
  max: number;
  onChange: (value: NegativeKeywordTarget[]) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const [matchTypes, setMatchTypes] = useState<NegativeMatchType[]>([
    "NEGATIVE_PHRASE",
    "NEGATIVE_EXACT",
  ]);
  const [mode, setMode] = useState<"input" | "library">("input");
  const [feedback, setFeedback] = useState<string | null>(null);
  const add = () => {
    const parsed = parseNegativeKeywords(input, matchTypes, value);
    if (value.length + parsed.added.length > max) {
      setFeedback(
        t("negatives.limit", {
          current: value.length,
          attempted: value.length + parsed.added.length,
          max,
        }),
      );
      return;
    }
    onChange([...value, ...parsed.added]);
    onInputChange(parsed.invalid.join(", "));
    setFeedback(
      parsed.duplicates.length
        ? t("negatives.duplicates", { count: parsed.duplicates.length })
        : parsed.invalid.length
          ? t("negatives.invalid", { count: parsed.invalid.length })
          : null,
    );
  };
  return (
    <div
      className="grid min-h-[420px] grid-cols-2 overflow-hidden border border-line"
      data-slot="negative-keyword-editor"
    >
      <div className="flex min-w-0 flex-col border-r border-line">
        <div className="flex h-10 items-end gap-6 border-b border-line px-4 text-xs font-medium">
          <button
            type="button"
            className={cn(
              "h-10 border-b-2 px-0 pt-1",
              mode === "input"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground",
            )}
            onClick={() => setMode("input")}
            data-negative-keyword-mode="input"
          >
            {t("negatives.keywords.enterList")}
          </button>
          <button
            type="button"
            className={cn(
              "h-10 border-b-2 px-0 pt-1",
              mode === "library"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground",
            )}
            onClick={() => setMode("library")}
            data-negative-keyword-mode="library"
          >
            {t("negatives.keywords.addFromList")}
          </button>
        </div>
        {mode === "input" ? (
          <div className="flex flex-1 flex-col gap-3 p-4">
            <FieldSet>
              <FieldLegend variant="label">
                <span className="text-destructive-foreground">*</span> {t("negatives.matchTypes")}:
              </FieldLegend>
              <FieldGroup className="flex flex-row gap-4">
                <Field orientation="horizontal">
                  <Checkbox
                    id="negative-phrase"
                    checked={matchTypes.includes("NEGATIVE_PHRASE")}
                    onCheckedChange={(checked) =>
                      setMatchTypes((current) =>
                        checked
                          ? [...current, "NEGATIVE_PHRASE"]
                          : current.filter((type) => type !== "NEGATIVE_PHRASE"),
                      )
                    }
                  />
                  <FieldLabel htmlFor="negative-phrase">{t("negatives.phrase")}</FieldLabel>
                </Field>
                <Field orientation="horizontal">
                  <Checkbox
                    id="negative-exact"
                    checked={matchTypes.includes("NEGATIVE_EXACT")}
                    onCheckedChange={(checked) =>
                      setMatchTypes((current) =>
                        checked
                          ? [...current, "NEGATIVE_EXACT"]
                          : current.filter((type) => type !== "NEGATIVE_EXACT"),
                      )
                    }
                  />
                  <FieldLabel htmlFor="negative-exact">{t("negatives.exact")}</FieldLabel>
                </Field>
              </FieldGroup>
            </FieldSet>
            <Textarea
              id="negative-keyword-input"
              value={input}
              onChange={(event) => onInputChange(event.target.value)}
              placeholder={t("negatives.keywords.placeholder")}
              className="min-h-36 flex-1 resize-none"
            />
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] text-muted-foreground">
                {t("negatives.keywords.maximum", { max })}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!input.trim() || matchTypes.length === 0}
                onClick={add}
              >
                {t("actions.add")}
              </Button>
            </div>
            {feedback ? <p className="text-xs text-warning-foreground">{feedback}</p> : null}
          </div>
        ) : (
          <EditorEmptyState label={t("negatives.noneAdded")} />
        )}
      </div>
      <TargetList
        title={t("negatives.added", { count: value.length })}
        primaryColumn={t("negatives.keywords.column")}
        rows={value.map((target) => ({
          key: `${target.text}:${target.matchType}`,
          primary: target.text,
          secondary: t(`negatives.match.${target.matchType}`),
        }))}
        onRemove={(key) =>
          onChange(value.filter((target) => `${target.text}:${target.matchType}` !== key))
        }
        onClear={() => onChange([])}
      />
    </div>
  );
}

export function NegativeProductEditor({
  value,
  input,
  onInputChange,
  max,
  onChange,
}: {
  value: string[];
  input: string;
  onInputChange: (input: string) => void;
  max: number;
  onChange: (value: string[]) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  const [mode, setMode] = useState<"search" | "input">("search");
  const [feedback, setFeedback] = useState<string | null>(null);
  const normalizedSearch = input.trim().toUpperCase();
  const searchResult = /^[A-Z0-9]{10}$/.test(normalizedSearch) ? normalizedSearch : null;
  const searchResultAdded = searchResult ? value.includes(searchResult) : false;
  const add = () => {
    const parsed = parseNegativeProducts(input, value);
    if (value.length + parsed.added.length > max) {
      setFeedback(
        t("negatives.limit", {
          current: value.length,
          attempted: value.length + parsed.added.length,
          max,
        }),
      );
      return;
    }
    onChange([...value, ...parsed.added]);
    onInputChange(parsed.invalid.join(" "));
    setFeedback(
      parsed.invalid.length
        ? t("negatives.asinOnly", { count: parsed.invalid.length })
        : parsed.duplicates.length
          ? t("negatives.duplicates", { count: parsed.duplicates.length })
          : null,
    );
  };
  return (
    <div
      className="grid min-h-[500px] grid-cols-2 overflow-hidden border border-line"
      data-slot="negative-product-editor"
    >
      <div className="flex min-w-0 flex-col border-r border-line">
        <div className="flex h-10 items-end border-b border-line px-4 text-xs font-medium">
          <span className="w-1/2 border-b-2 border-primary pb-2 text-center text-primary">
            {t("negatives.products.exclude")}
          </span>
        </div>
        <div className="flex h-9 items-end gap-6 border-b border-line px-4 text-xs">
          <button
            type="button"
            className={cn(
              "h-9 border-b-2 px-0 pt-1",
              mode === "search"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground",
            )}
            onClick={() => {
              setMode("search");
              setFeedback(null);
            }}
            data-negative-product-mode="search"
          >
            {t("products.tabs.search")}
          </button>
          <button
            type="button"
            className={cn(
              "h-9 border-b-2 px-0 pt-1",
              mode === "input"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground",
            )}
            onClick={() => {
              setMode("input");
              setFeedback(null);
            }}
            data-negative-product-mode="input"
          >
            {t("negatives.keywords.enterList")}
          </button>
        </div>
        {mode === "search" ? (
          <div className="flex min-h-0 flex-1 flex-col p-4">
            <div className="relative">
              <IconSearch className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="negative-product-input"
                value={input}
                onChange={(event) => onInputChange(event.target.value)}
                placeholder={t("negatives.products.placeholder")}
                className="pl-9"
              />
            </div>
            {searchResult ? (
              <div className="mt-3 flex items-center justify-between border-y border-line px-3 py-2">
                <div>
                  <p className="font-mono text-xs font-medium">{searchResult}</p>
                  <p className="text-[10px] text-muted-foreground">ASIN</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  disabled={searchResultAdded}
                  onClick={() => {
                    onChange([...value, searchResult]);
                    onInputChange("");
                  }}
                >
                  {searchResultAdded ? t("products.enter.added") : t("actions.add")}
                </Button>
              </div>
            ) : (
              <EditorEmptyState label={t("negatives.noneAdded")} compact />
            )}
          </div>
        ) : (
          <div className="flex flex-1 flex-col gap-3 p-4">
            <Textarea
              id="negative-product-input"
              value={input}
              onChange={(event) => onInputChange(event.target.value)}
              placeholder={t("negatives.products.enterPlaceholder")}
              className="h-40 resize-none"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-end"
              disabled={!input.trim()}
              onClick={add}
            >
              {t("actions.add")}
            </Button>
            {feedback ? <p className="text-xs text-warning-foreground">{feedback}</p> : null}
          </div>
        )}
      </div>
      <TargetList
        title={t("negatives.added", { count: value.length })}
        primaryColumn={t("negatives.products.column")}
        rows={value.map((asin) => ({ key: asin, primary: asin, secondary: "ASIN" }))}
        onRemove={(key) => onChange(value.filter((asin) => asin !== key))}
        onClear={() => onChange([])}
      />
    </div>
  );
}

function TargetList({
  title,
  primaryColumn,
  rows,
  onRemove,
  onClear,
}: {
  title: string;
  primaryColumn: string;
  rows: Array<{ key: string; primary: string; secondary: string }>;
  onRemove: (key: string) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <div className="flex min-w-0 flex-col">
      <div className="flex h-10 items-center justify-between border-b border-line px-4 text-xs font-medium">
        <span>{title}</span>
        <AlertDialog>
          <AlertDialogTrigger
            render={<Button type="button" variant="ghost" size="xs" disabled={!rows.length} />}
          >
            {t("negatives.removeAllAction")}
          </AlertDialogTrigger>
          <AlertDialogPopup>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("negatives.removeAll.title")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("negatives.removeAll.description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogClose render={<Button variant="outline" />}>
                {t("actions.cancel")}
              </AlertDialogClose>
              <AlertDialogClose render={<Button variant="destructive" onClick={onClear} />}>
                {t("actions.remove")}
              </AlertDialogClose>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      </div>
      <div className="grid grid-cols-[1fr_90px] border-b border-line bg-muted/25 px-4 py-2 text-[11px] font-medium">
        <span>{primaryColumn}</span>
        <span className="text-right">{t("negatives.action")}</span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {rows.length ? (
          rows.map((row) => (
            <div
              key={row.key}
              className="flex items-center justify-between gap-2 border-b border-line px-4 py-2 last:border-b-0"
            >
              <div className="min-w-0">
                <p className="truncate text-xs">{row.primary}</p>
                <p className="text-[10px] text-muted-foreground">{row.secondary}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label={t("negatives.removeTarget", { target: row.primary })}
                onClick={() => onRemove(row.key)}
              >
                <IconTrash />
              </Button>
            </div>
          ))
        ) : (
          <EditorEmptyState label={t("negatives.noneAdded")} />
        )}
      </div>
    </div>
  );
}
